import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyDirective } from 'ngx-currency';
import { CriterioCotacaoEnum } from '@shared/enums/compras/criterio-cotacao.enum';
import { MaskService } from '@shared/services/mask.service';
import { FormaPagamento } from '@shared/services/models/financas/forma-pagamento.model';
import { TipoFreteStruct } from '@shared/services/structs/compras/tipo-frete.struct';
import {
  COTACAO_CRITERIOS,
  CotacaoParcela,
  CotacaoParcelasState,
  inicioDoDia,
  isOfertaElegivelCotacao,
} from '../card-edit-dialog/card-edit-dialog.types';
import { CotacaoParcelasEditorComponent } from '../cotacao-parcelas-editor/cotacao-parcelas-editor.component';

export interface MatrixProduct {
  /** Stable per-row key used to index offers/selections (String(idCompraItem) for real data). */
  key: string;
  idCompraItem: number;
  sku: string;
  name: string;
  qty: number;
  unit: string;
}

/**
 * Identidade e exibição da coluna. Prazo, pagamento e frete NÃO ficam aqui: são editáveis no
 * painel, então a matriz os deriva de `offers`/`headers` para não exibir valor velho.
 */
export interface MatrixSupplier {
  /** Stable per-column key (String(idCotacaoFornecedor) for real data). */
  id: string;
  idCotacaoFornecedor: number;
  idFornecedor: number;
  initials: string;
  color: string;
  name: string;
  tag: string;
  /** Cotação respondida (datetimeResposta) — condição de elegibilidade para a sugestão automática. */
  respondido: boolean;
}

export interface MatrixOffer {
  // Quantidade que o COMPRADOR decide pedir DESTE fornecedor para este item — editável na
  // matriz (cotacao_fornecedor_item.quantidade). Base do subtotal. Aceita decimal.
  qty: number;
  // Disponibilidade declarada pelo fornecedor (portal/planilha); editável no painel lateral.
  disponibilidade?: number | null;
  /** Preço unitário numérico, sem arredondar a 2 casas; null = não cotou. */
  price: number | null;
  prazoEntregaDias?: number | null;
  // Impostos informados pelo fornecedor (valores totais do item, não por unidade).
  valorIpi?: number | null;
  valorIcmsSt?: number | null;
  marcaOfertada?: string | null;
  observacoes?: string | null;
  // Alternativa ofertada pelo fornecedor para este item (opcional).
  alternativaDescricao?: string | null;
  alternativaMarca?: string | null;
  alternativaValorUnitario?: number | null;
}

/** Cabeçalho editável da cotação — o que não é por item. */
export interface MatrixSupplierHeader {
  /** `expr` é a condição de pagamento ("30 60"); `parcelas` é a grade derivada dela. */
  pagamento: CotacaoParcelasState;
  idTipoFrete: number | null;
  /** Número (input com `[currencyMask]`); 0 = frete grátis, null = não informado. */
  valorFrete: number | null;
  validadeDias: number | null;
  observacoes: string;
}

export interface ComparisonMatrixData {
  criterio?: CriterioCotacaoEnum;
  products?: MatrixProduct[];
  suppliers?: MatrixSupplier[];
  offers?: Record<string, Record<string, MatrixOffer>>;
  encargos?: Record<string, number | null>;
  /** Cabeçalho da cotação por fornecedor (chave = MatrixSupplier.id) para o painel de edição. */
  headers?: Record<string, MatrixSupplierHeader>;
  /** Lista de referência do select de tipo de frete. */
  tiposFrete?: TipoFreteStruct[];
  /** Formas de pagamento do select das parcelas (cadastro de Finanças). */
  formasPagamento?: FormaPagamento[];
  /** Data-base do editor de parcelas — a MESMA da etapa, para dias↔data não divergirem. */
  dataBaseParcelas?: Date;
  /** Seleção vigente no "Pedido montado" — chave = MatrixProduct.key, valor = MatrixSupplier.id. */
  selections?: Record<string, string>;
}

export interface MatrixSelectionItem {
  /**
   * Identidade real da linha. Reconciliar por `sku` não funciona: produto avulso e itens de
   * catálogo sem SKU têm sku vazio, e todos casariam entre si (ou com nenhum) — a seleção
   * chegava sem idCompraItem e não era persistida.
   */
  idCompraItem: number;
  sku: string;
  name: string;
  qty: number;
  supplier: string;
  supplierColor: string;
  /** Identidade real do fornecedor selecionado — usar isto para reconciliar, nunca `supplier` (nome exibido, não é único). */
  idFornecedor: number;
  subtotal: number;
  /** Preço unitário efetivamente ofertado (não derivar de subtotal/qty — perde precisão em arredondamentos). */
  valorUnitario: number | null;
}

export interface MatrixSupplierEditItem {
  idCompraItem: number;
  valorUnitario: number | null;
  quantidade: number | null;
  disponibilidade: number | null;
  prazoEntregaDias: number | null;
  marcaOfertada: string | null;
  valorIpi: number | null;
  valorIcmsSt: number | null;
  observacoes: string | null;
}

export interface MatrixSupplierEdit {
  idCotacaoFornecedor: number;
  valorEncargos: number | null;
  marcarComoRespondida: boolean;
  /** Cabeçalho editado no painel lateral. */
  condicaoPagamento: string | null;
  idTipoFrete: number | null;
  valorFrete: number | null;
  validadeDias: number | null;
  observacoes: string | null;
  parcelas: CotacaoParcela[] | null;
  /** O comprador mexeu na cotação deste fornecedor nesta sessão (marca origem como Manual). */
  ajustadoPeloComprador: boolean;
  itens: MatrixSupplierEditItem[];
}

export interface ComparisonMatrixResult {
  selection: MatrixSelectionItem[];
  criterio: CriterioCotacaoEnum;
  edits: MatrixSupplierEdit[];
}

interface SelectionGroup {
  supplier: MatrixSupplier;
  products: { product: MatrixProduct; offer: MatrixOffer }[];
  total: number;
  partialCount: number;
}

@Component({
  selector: 'app-comparison-matrix-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgxCurrencyDirective,
    CotacaoParcelasEditorComponent,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
  ],
  templateUrl: './comparison-matrix-dialog.component.html',
  styleUrl: './comparison-matrix-dialog.component.css',
})
export class ComparisonMatrixDialogComponent {
  products: MatrixProduct[];
  suppliers: MatrixSupplier[];
  criterios = COTACAO_CRITERIOS;

  offers: Record<string, Record<string, MatrixOffer>>;
  encargos: Record<string, number | null>;
  selections: Record<string, string> = {};
  // Por item selecionado: true quando a variante escolhida é a ALTERNATIVA do fornecedor.
  altSelections: Record<string, boolean> = {};
  criterio: CriterioCotacaoEnum;
  showSuggestion = true;

  /** Identifier of the cell currently being edited, e.g. `price:dell:MS-LOG-MX`. */
  editingKey: string | null = null;

  // ----- Painel lateral de edição da cotação -----
  /** Fornecedor com o painel aberto (null = painel fechado). */
  editingSupplierId: string | null = null;
  headers: Record<string, MatrixSupplierHeader> = {};
  tiposFrete: TipoFreteStruct[] = [];
  formasPagamento: FormaPagamento[] = [];
  dataBaseParcelas: Date;
  /** Fornecedores cuja cotação o comprador ajustou nesta sessão — vira selo e origem Manual. */
  ajustados: Record<string, boolean> = {};

  moedaMask: ReturnType<MaskService['getDynamicCurrencyMask']>;
  quantidadeMask: ReturnType<MaskService['getDynamicDecimalMask']>;
  inteiroMask: ReturnType<MaskService['getIntegerMask']>;
  isMaximizado = false;

  constructor(
    public dialogRef: MatDialogRef<ComparisonMatrixDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ComparisonMatrixData,
    maskService: MaskService,
  ) {
    this.moedaMask = maskService.getDynamicCurrencyMask(null, false);
    this.quantidadeMask = maskService.getDynamicDecimalMask(null, false);
    this.inteiroMask = maskService.getIntegerMask();
    this.products = data?.products ?? [];
    this.suppliers = data?.suppliers ?? [];
    this.offers = JSON.parse(JSON.stringify(data?.offers ?? {}));
    this.encargos = { ...(data?.encargos ?? {}) };
    this.criterio = data?.criterio ?? CriterioCotacaoEnum.MenorPrecoTotal;
    this.tiposFrete = data?.tiposFrete ?? [];
    this.formasPagamento = data?.formasPagamento ?? [];
    this.dataBaseParcelas = data?.dataBaseParcelas ?? inicioDoDia(new Date());
    // Cópia profunda: o painel edita à vontade e nada vaza para a etapa se o usuário cancelar.
    for (const s of this.suppliers) {
      const origem = data?.headers?.[s.id];
      this.headers[s.id] = origem
        ? {
            ...origem,
            pagamento: { expr: origem.pagamento.expr, parcelas: origem.pagamento.parcelas.map(p => ({ ...p })) },
          }
        : {
            pagamento: { expr: '', parcelas: [] },
            idTipoFrete: null,
            valorFrete: null,
            validadeDias: null,
            observacoes: '',
          };
    }
    // Reabre com a escolha vigente; só oferece o "Sugerido" quando ainda não há decisão.
    // Descarta o que não é selecionável aqui (oferta sumiu, preço zerado): manter a chave
    // levaria um item de preço zero para o pedido montado ao salvar.
    this.selections = Object.fromEntries(
      Object.entries(data?.selections ?? {}).filter(([key, sid]) => this.canSelect(sid, key)),
    );
    this.showSuggestion = this.hasNoSelections();
  }

  // ----- Helpers -----
  fmt(n: number): string {
    return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /** Preço unitário para exibição: até 4 casas (o preço não é arredondado a 2); zero/ausente → "—". */
  private fmtPreco(n: number | null | undefined): string {
    if (!n || n <= 0) return '—';
    return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  }

  displayOfferPrice(sid: string, key: string): string {
    return this.fmtPreco(this.offerPrice(sid, key));
  }

  displayAltPrice(sid: string, key: string): string {
    return this.fmtPreco(this.altPrice(sid, key));
  }

  displayEncargo(sid: string): string {
    const v = this.encargoFor(sid);
    return v != null && v > 0 ? this.fmt(v) : '—';
  }

  displayTotalBySupplier(sid: string): string {
    if (!this.supplierHasPricing(sid)) return '—';
    return this.fmt(this.totalBySupplier(sid));
  }

  supplierHasPricing(sid: string): boolean {
    return this.products.some(p => this.offerPrice(sid, p.key) > 0);
  }

  getOffer(sid: string, key: string): MatrixOffer | undefined {
    return this.offers[sid]?.[key];
  }

  // Quantidade originalmente SOLICITADA (Solicitação) — só para referência/label no produto;
  // não é a base do subtotal (que usa offer.qty, a decisão de compra por fornecedor).
  qtdSolicitada(key: string): number {
    return this.products.find(p => p.key === key)?.qty ?? 0;
  }

  // Disponibilidade que o fornecedor declarou ter — só leitura.
  offerDisponibilidade(sid: string, key: string): number | null {
    return this.getOffer(sid, key)?.disponibilidade ?? null;
  }

  /** Mesma regra de elegibilidade da etapa (isOfertaElegivelCotacao): respondido, com preço e disponibilidade. */
  private elegivel(sid: string, key: string): boolean {
    const o = this.getOffer(sid, key);
    return isOfertaElegivelCotacao(!!this.supplierById(sid)?.respondido, o?.price, o?.disponibilidade);
  }

  lineTotal(sid: string, key: string): number {
    const o = this.getOffer(sid, key);
    // Custo do item = preço × quantidade que o comprador decide pedir DESTE fornecedor + impostos.
    return (o?.price ?? 0) * (o?.qty || 0) + this.impostoItem(sid, key);
  }

  /**
   * Base do "Pedido montado": preço efetivo × quantidade, SEM impostos nem encargos — o mesmo
   * que é gravado na compra, para o total do resumo não divergir do da etapa Cotação.
   */
  lineSubtotal(sid: string, key: string): number {
    return this.precoEfetivo(sid, key) * (this.getOffer(sid, key)?.qty || 0);
  }

  /** Idem, mas sempre pela oferta original — projeções de sugestão nunca usam a alternativa. */
  private lineSubtotalOriginal(sid: string, key: string): number {
    const o = this.getOffer(sid, key);
    return (o?.price ?? 0) * (o?.qty || 0);
  }

  // Impostos (IPI + ST) de um item — valores totais do item, só quando há oferta com preço.
  impostoItem(sid: string, key: string): number {
    const o = this.getOffer(sid, key);
    if (!o || !((o.price ?? 0) > 0)) return 0;
    return (o.valorIpi ?? 0) + (o.valorIcmsSt ?? 0);
  }

  // Soma dos impostos (IPI + ST) de todos os itens ofertados por um fornecedor.
  impostosBySupplier(sid: string): number {
    return this.products.reduce((sum, p) => sum + this.impostoItem(sid, p.key), 0);
  }

  // Detalhamento IPI × ST somado de todos os itens — para o breakdown abaixo do total.
  impostosBreakdown(sid: string): { ipi: number; st: number } {
    return this.products.reduce((acc, p) => {
      const o = this.getOffer(sid, p.key);
      if (o && (o.price ?? 0) > 0) {
        acc.ipi += o.valorIpi ?? 0;
        acc.st += o.valorIcmsSt ?? 0;
      }
      return acc;
    }, { ipi: 0, st: 0 });
  }

  // ----- Inline editing -----
  isEditing(key: string): boolean {
    return this.editingKey === key;
  }

  startEdit(key: string): void {
    this.editingKey = key;
  }

  stopEdit(): void {
    this.editingKey = null;
  }

  offerPrice(sid: string, key: string): number {
    return this.getOffer(sid, key)?.price ?? 0;
  }

  hasAlternativa(sid: string, key: string): boolean {
    const o = this.getOffer(sid, key);
    return !!(o && (o.alternativaDescricao || o.alternativaMarca || o.alternativaValorUnitario != null));
  }

  altLabel(sid: string, key: string): string {
    const o = this.getOffer(sid, key);
    return o?.alternativaDescricao || o?.alternativaMarca || 'Alternativa';
  }

  altMarca(sid: string, key: string): string {
    return this.getOffer(sid, key)?.alternativaMarca ?? '';
  }

  altPrice(sid: string, key: string): number {
    return this.getOffer(sid, key)?.alternativaValorUnitario ?? 0;
  }

  // Preço unitário efetivo do que está selecionado (original ou alternativa) para o item.
  private precoEfetivo(sid: string, key: string): number {
    return this.altSelections[key] && this.selections[key] === sid
      ? this.altPrice(sid, key)
      : this.offerPrice(sid, key);
  }

  setOfferPrice(sid: string, key: string, value: number | null): void {
    const cur = this.offers[sid]?.[key] || { qty: 0, price: null };
    this.offers[sid] = { ...(this.offers[sid] || {}), [key]: { ...cur, price: value } };
    this.marcarAjustado(sid);
  }

  offerQty(sid: string, key: string): number {
    return this.getOffer(sid, key)?.qty ?? 0;
  }

  setOfferQty(sid: string, key: string, value: number | null): void {
    const cur = this.offers[sid]?.[key] || { qty: 0, price: null };
    this.offers[sid] = { ...(this.offers[sid] || {}), [key]: { ...cur, qty: value ?? 0 } };
    this.marcarAjustado(sid);
  }

  encargoFor(sid: string): number | null {
    return this.encargos[sid] ?? null;
  }

  setEncargo(sid: string, value: number | null): void {
    this.encargos = { ...this.encargos, [sid]: value };
    this.marcarAjustado(sid);
  }

  /**
   * "Não cotou" = sem preço informado (nem da oferta, nem da alternativa). A quantidade não entra:
   * a API semeia cada item com a quantidade solicitada ao convidar o fornecedor. Célula em edição
   * nunca é vazia — senão o input aberto por `fillEmpty` sumiria antes de receber o preço.
   */
  isEmpty(sid: string, key: string): boolean {
    if (this.isEditing(`price:${sid}:${key}`) || this.isEditing(`qty:${sid}:${key}`)) return false;
    return !(this.offerPrice(sid, key) > 0) && !(this.altPrice(sid, key) > 0);
  }

  // Preserva o que a linha já tem (semeada pela API ou ajustada no painel): só faltava o preço.
  fillEmpty(sid: string, key: string, requestedQty: number): void {
    const cur = this.offers[sid]?.[key];
    this.offers[sid] = { ...(this.offers[sid] || {}), [key]: { ...cur, qty: cur?.qty || requestedQty, price: null } };
    this.startEdit(`price:${sid}:${key}`);
  }

  // "Parcial": o fornecedor declarou ter menos do que o comprador decidiu pedir dele.
  isPartial(sid: string, key: string): boolean {
    const disp = this.offerDisponibilidade(sid, key);
    if (disp == null) return false;
    return disp < this.offerQty(sid, key);
  }

  // ----- Derived: melhor fornecedor por produto conforme o critério -----
  bestSupplierForKey(key: string): string | null {
    // No modo manual não há sugestão automática — o comprador escolhe.
    if (this.criterio === CriterioCotacaoEnum.DefinidoManualmente) return null;

    let bestScore = Infinity;
    let bestPrice = Infinity;
    let bestSid: string | null = null;
    for (const s of this.suppliers) {
      if (!this.elegivel(s.id, key)) continue;
      const o = this.getOffer(s.id, key);
      const price = this.offerPrice(s.id, key);

      let score: number;
      if (this.criterio === CriterioCotacaoEnum.MenorPrazoEntrega) {
        score = o?.prazoEntregaDias ?? Number.POSITIVE_INFINITY;
      } else if (this.criterio === CriterioCotacaoEnum.MenorFrete) {
        // Frete lido do cabeçalho editável: mudar o frete no painel re-pontua na hora.
        // Frete 0 é o melhor; só o não informado vai para o fim.
        score = this.freteFor(s.id) ?? Number.POSITIVE_INFINITY;
      } else {
        score = price;
      }

      if (score < bestScore || (score === bestScore && price < bestPrice)) {
        bestScore = score;
        bestPrice = price;
        bestSid = s.id;
      }
    }
    return bestSid;
  }

  /** Recalcula a sugestão ao trocar o critério (zera seleção manual anterior). */
  onCriterioChange(): void {
    this.selections = {};
    this.altSelections = {};
    this.showSuggestion = true;
  }

  bestPriceForKey(key: string): number | null {
    let best = Infinity;
    for (const s of this.suppliers) {
      const v = this.offerPrice(s.id, key);
      if (this.elegivel(s.id, key) && v < best) best = v;
    }
    return isFinite(best) ? best : null;
  }

  isBest(sid: string, p: MatrixProduct): boolean {
    return this.bestSupplierForKey(p.key) === sid;
  }

  isSelected(sid: string, key: string): boolean {
    return this.selections[key] === sid;
  }

  // Variante selecionada: original (alt=false) ou alternativa (alt=true).
  isVariantSelected(sid: string, key: string, alt: boolean): boolean {
    return this.selections[key] === sid && !!this.altSelections[key] === alt;
  }

  canSelect(sid: string, key: string): boolean {
    const o = this.getOffer(sid, key);
    return !!o && (o.qty || 0) > 0 && (o.price ?? 0) > 0;
  }

  canSelectVariant(sid: string, key: string, alt: boolean): boolean {
    if (!alt) return this.canSelect(sid, key);
    const o = this.getOffer(sid, key);
    return !!o && (o.qty || 0) > 0 && this.altPrice(sid, key) > 0;
  }

  hasNoSelections(): boolean {
    return Object.keys(this.selections).length === 0;
  }

  isSuggested(sid: string, p: MatrixProduct): boolean {
    return this.showSuggestion && this.hasNoSelections() && this.bestSupplierForKey(p.key) === sid;
  }

  deltaPct(sid: string, p: MatrixProduct): number {
    const best = this.bestPriceForKey(p.key);
    const price = this.offerPrice(sid, p.key);
    if (!best || price <= 0 || best <= 0) return 0;
    return ((price - best) / best) * 100;
  }

  // ----- Selection -----
  selectFor(key: string, sid: string, alt = false): void {
    if (!this.canSelectVariant(sid, key, alt)) return;
    // Clicar na variante já selecionada desmarca; senão seleciona esse fornecedor+variante.
    if (this.isVariantSelected(sid, key, alt)) {
      const { [key]: _removed, ...rest } = this.selections;
      const { [key]: _removedAlt, ...restAlt } = this.altSelections;
      this.selections = rest;
      this.altSelections = restAlt;
    } else {
      this.selections = { ...this.selections, [key]: sid };
      this.altSelections = { ...this.altSelections, [key]: alt };
    }
  }

  electAll(sid: string): void {
    const next = { ...this.selections };
    const nextAlt = { ...this.altSelections };
    for (const p of this.products) {
      if (!this.canSelect(sid, p.key)) continue;
      next[p.key] = sid;
      // Eleger em massa usa sempre a oferta original: sem zerar o flag, um item que estava na
      // alternativa de outro fornecedor cairia na alternativa inexistente deste (preço 0).
      nextAlt[p.key] = false;
    }
    this.selections = next;
    this.altSelections = nextAlt;
  }

  /** Há sugestão aplicável pelo critério atual (o modo manual não sugere). */
  hasSuggestion(): boolean {
    return this.criterio !== CriterioCotacaoEnum.DefinidoManualmente
      && this.products.some(p => this.bestSupplierForKey(p.key) != null);
  }

  applySuggestion(): void {
    const next: Record<string, string> = {};
    for (const p of this.products) {
      const sid = this.bestSupplierForKey(p.key);
      if (sid) next[p.key] = sid;
    }
    this.selections = next;
    this.altSelections = {}; // sugestão usa sempre a oferta original
    this.showSuggestion = false;
  }

  // ----- Aggregates -----
  totalBySupplier(sid: string): number {
    const items = this.products.reduce((sum, p) => sum + this.lineTotal(sid, p.key), 0);
    return items + (this.encargos[sid] ?? 0);
  }

  winCount(sid: string): number {
    return this.products.filter(p => this.bestSupplierForKey(p.key) === sid).length;
  }

  selectedCount(sid: string): number {
    return Object.values(this.selections).filter(v => v === sid).length;
  }

  suggestedTotal(): number {
    return this.products.reduce((sum, p) => {
      const sid = this.bestSupplierForKey(p.key);
      return sum + (sid ? this.lineSubtotalOriginal(sid, p.key) : 0);
    }, 0);
  }

  selectedTotal(): number {
    return this.products.reduce((sum, p) => {
      const sid = this.selections[p.key];
      return sum + (sid ? this.lineSubtotal(sid, p.key) : 0);
    }, 0);
  }

  respondedCount(): number {
    return this.suppliers.filter(s => this.supplierHasPricing(s.id)).length;
  }

  /** Fornecedores elegíveis em TODOS os itens (fonte única viável). */
  private fonteUnicaViavel(): MatrixSupplier[] {
    return this.suppliers.filter(s => this.products.every(p => this.elegivel(s.id, p.key)));
  }

  cheapestProposalTotal(): number | null {
    const totals = this.fonteUnicaViavel().map(s => this.totalBySupplier(s.id));
    return totals.length ? Math.min(...totals) : null;
  }

  isCheapestProposal(sid: string): boolean {
    const t = this.totalBySupplier(sid);
    return t > 0 && t === this.cheapestProposalTotal();
  }

  /** Produtos ainda sem fornecedor escolhido — informativo no rodapé, não bloqueia o salvamento. */
  missingCount(): number {
    return this.products.length - Object.keys(this.selections).length;
  }

  supplierById(sid: string): MatrixSupplier | undefined {
    return this.suppliers.find(s => s.id === sid);
  }

  // ----- Selection summary -----
  get selectionGroups(): SelectionGroup[] {
    const groups: Record<string, SelectionGroup> = {};
    for (const p of this.products) {
      const sid = this.selections[p.key];
      if (!sid) continue;
      const offer = this.getOffer(sid, p.key);
      if (!offer) continue;
      if (!groups[sid]) {
        groups[sid] = { supplier: this.supplierById(sid)!, products: [], total: 0, partialCount: 0 };
      }
      groups[sid].products.push({ product: p, offer });
      groups[sid].total += this.lineSubtotal(sid, p.key);
      if (offer.qty < p.qty) groups[sid].partialCount += 1;
    }
    return Object.values(groups);
  }

  get grandTotal(): number {
    return this.selectionGroups.reduce((sum, g) => sum + g.total, 0);
  }

  /** Impostos (IPI+ST) da seleção — exibidos à parte: não entram no valor gravado na compra. */
  get selectedImpostos(): number {
    return this.products.reduce((sum, p) => {
      const sid = this.selections[p.key];
      return sum + (sid ? this.impostoItem(sid, p.key) : 0);
    }, 0);
  }

  /**
   * Economia do split frente à melhor proposta de fonte única, na mesma base do resumo.
   * Comparar com `suggestedTotal` era inútil: ela é ótima por definição no critério de preço.
   */
  get savings(): number {
    const totais = this.fonteUnicaViavel()
      .map(s => this.products.reduce((sum, p) => sum + this.lineSubtotalOriginal(s.id, p.key), 0));
    if (!totais.length || this.grandTotal <= 0) return 0;
    return Math.min(...totais) - this.grandTotal;
  }

  // ----- Grid template -----
  /** Largura máxima de uma coluna de fornecedor, em px. */
  private readonly colunaMaxPx = 340;

  get gridColumns(): string {
    return `300px repeat(${this.suppliers.length}, minmax(220px, 1fr))`;
  }

  /**
   * Teto de largura da moldura da grade e da barra do pedido montado. Em tela cheia com poucos
   * fornecedores, sem teto cada célula esticava para ~600px. Limitar a moldura em vez das
   * colunas mantém os tracks `1fr` preenchendo a borda, sem vão branco.
   */
  get contentMaxWidth(): string {
    return `min(100%, ${300 + this.suppliers.length * this.colunaMaxPx}px)`;
  }

  get gridMinWidth(): string {
    return `${300 + this.suppliers.length * 230}px`;
  }

  get sectionSpan(): string {
    return `1 / span ${this.suppliers.length + 1}`;
  }

  // ----- Painel lateral: edição completa da cotação -----
  headerFor(sid: string): MatrixSupplierHeader {
    return this.headers[sid];
  }

  get editingSupplier(): MatrixSupplier | undefined {
    return this.editingSupplierId ? this.supplierById(this.editingSupplierId) : undefined;
  }

  openEditor(sid: string): void {
    // O painel edita por ngModel direto, então precisa de um objeto por produto mesmo onde o
    // fornecedor não cotou. Sem preço, `{qty:0, price:null}` segue sendo "não cotou" para a grade.
    for (const p of this.products) {
      if (!this.offers[sid]?.[p.key]) {
        this.offers[sid] = { ...(this.offers[sid] || {}), [p.key]: { qty: 0, price: null } };
      }
    }
    this.editingSupplierId = sid;
    this.stopEdit();
  }

  closeEditor(): void {
    this.editingSupplierId = null;
  }

  /** Navega entre fornecedores sem fechar o painel (‹ ›). */
  gotoEditor(delta: number): void {
    if (!this.suppliers.length) return;
    const atual = this.suppliers.findIndex(s => s.id === this.editingSupplierId);
    const proximo = (atual + delta + this.suppliers.length) % this.suppliers.length;
    this.openEditor(this.suppliers[proximo].id);
  }

  /** Toda edição feita aqui é decisão do comprador — vira selo na coluna e origem Manual. */
  marcarAjustado(sid: string): void {
    this.ajustados[sid] = true;
  }

  foiAjustado(sid: string): boolean {
    return !!this.ajustados[sid];
  }

  /** Frete vigente (já considerando o que foi editado no painel); null = não informado. */
  freteFor(sid: string): number | null {
    return this.headers[sid]?.valorFrete ?? null;
  }

  /** Condição de pagamento vigente — reflete a edição do painel na linha da grade. */
  paymentLabel(sid: string): string {
    return this.headers[sid]?.pagamento.expr.trim() || '—';
  }

  /** Maior prazo ofertado. Derivado dos itens para a linha reagir à edição no painel. */
  leadTimeLabel(sid: string): string {
    const prazos = this.products
      .map(p => this.getOffer(sid, p.key)?.prazoEntregaDias)
      .filter((n): n is number => n != null);
    return prazos.length ? `${Math.max(...prazos)} dias úteis` : '—';
  }

  validadeLabel(sid: string): string {
    const dias = this.headers[sid]?.validadeDias;
    return dias != null && dias > 0 ? `${dias} dias` : '—';
  }

  // ----- Moldura -----
  /** Abre em 96vw × 92vh; maximizar leva à tela cheia sem cantos, como o vínculo NF × pedido. */
  alternarTamanho(): void {
    this.isMaximizado = !this.isMaximizado;
    if (this.isMaximizado) {
      this.dialogRef.updateSize('100vw', '100vh');
      this.dialogRef.addPanelClass('associacao-dialog-maximizado');
    } else {
      this.dialogRef.updateSize('96vw', '92vh');
      this.dialogRef.removePanelClass('associacao-dialog-maximizado');
    }
    // Clique deixa o mat-icon-button com foco (e a sombra cinza); teclado continua com :focus-visible.
    (document.activeElement as HTMLElement | null)?.blur?.();
  }

  // ----- Footer -----
  close(): void {
    this.dialogRef.close(null);
  }

  /**
   * Grava as edições da cotação e aplica a seleção que existir. Seleção parcial é válida: exigir
   * um fornecedor por produto deixava sem como salvar quem só quisesse corrigir um preço.
   */
  apply(): void {
    const selection: MatrixSelectionItem[] = this.products
      .filter(p => this.selections[p.key])
      .map(p => {
        const sid = this.selections[p.key];
        const supplier = this.supplierById(sid)!;
        // Preço efetivo = alternativa quando a variante alternativa está selecionada.
        const price = this.precoEfetivo(sid, p.key);
        // Quantidade do pedido = decisão do comprador para o fornecedor VENCEDOR (editável na
        // matriz), não a disponibilidade que ele declarou ter.
        const qty = this.offerQty(sid, p.key);
        const alt = !!this.altSelections[p.key];
        return {
          idCompraItem: p.idCompraItem,
          sku: p.sku,
          name: alt ? `${p.name} — ${this.altLabel(sid, p.key)}` : p.name,
          qty,
          supplier: supplier.name,
          supplierColor: supplier.color,
          idFornecedor: supplier.idFornecedor,
          subtotal: price * qty,
          valorUnitario: price > 0 ? price : null,
        };
      });

    this.dialogRef.close({ selection, criterio: this.criterio, edits: this.montarEdits() } as ComparisonMatrixResult);
  }

  /** Edições persistíveis dos fornecedores que o comprador ajustou. */
  private montarEdits(): MatrixSupplierEdit[] {
    return this.suppliers
      .map(s => {
        const header = this.headers[s.id];
        const itens: MatrixSupplierEditItem[] = this.products
          .map(p => {
            const o = this.getOffer(s.id, p.key);
            const price = o?.price ?? 0;
            const qty = o?.qty || 0;
            return {
              idCompraItem: p.idCompraItem,
              valorUnitario: price > 0 ? price : null,
              quantidade: qty > 0 ? qty : null,
              disponibilidade: o?.disponibilidade ?? null,
              prazoEntregaDias: o?.prazoEntregaDias ?? null,
              marcaOfertada: o?.marcaOfertada?.trim() || null,
              valorIpi: o?.valorIpi ?? null,
              valorIcmsSt: o?.valorIcmsSt ?? null,
              observacoes: o?.observacoes?.trim() || null,
            };
          });
        return {
          idCotacaoFornecedor: s.idCotacaoFornecedor,
          valorEncargos: this.encargos[s.id] || null,
          marcarComoRespondida: itens.some(i => i.valorUnitario != null),
          condicaoPagamento: header?.pagamento.expr.trim() || null,
          idTipoFrete: header?.idTipoFrete ?? null,
          valorFrete: this.freteFor(s.id),
          validadeDias: header?.validadeDias ?? null,
          observacoes: header?.observacoes?.trim() || null,
          parcelas: header ? header.pagamento.parcelas : null,
          ajustadoPeloComprador: this.foiAjustado(s.id),
          itens,
        };
      })
      // Só quem o comprador realmente tocou: gravar todo fornecedor com preço fazia um PUT por
      // fornecedor a cada "Salvar", reescrevendo a origem de cotações vindas do portal como
      // Manual (perdendo a proveniência) e regravando as parcelas de todos sem necessidade.
      .filter(e => e.ajustadoPeloComprador);
  }

}
